import { RecordListScroll } from '@liza/ui/components/RecordListScroll';
import { useInfiniteRecordList } from '@liza/ui/hooks/useInfiniteRecordList';
import { useTRPC, useTRPCClient } from '@/utils/trpc';
import { parseISO } from 'date-fns';
import { vitalSignsValueSet } from '@liza/medplum-config/valueSet';
import type { PatientAdminApiRouterOutputs } from '@liza/patient-admin-api';
import { AddVitalFormContent, type AddVitalFormValues } from '@liza/ui/components/records/AddVitalFormContent';
import { BP_DIASTOLIC_CODE, BP_PANEL_CODE, BP_SYSTOLIC_CODE } from '@liza/ui/utils/observation';
import { VitalsList } from '@liza/ui/components/records/VitalsList';
import {
  type RecordsListFilters,
  defaultFilters,
  RecordsListControls,
} from '@liza/ui/components/records/RecordsListControls';
import type { ValueSetCode } from '@liza/ui/components/records/ValueSetCombobox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@liza/ui/components/ui/dialog';
import { useValueSetExpand } from '@liza/ui/hooks/useValueSetExpand';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useDebouncedCallback } from 'use-debounce';
import type { DetailPanelView } from '../types';
import { parseVitalLookupProps } from './vitalsLookup';

type Patient = PatientAdminApiRouterOutputs['patient']['get']['patient'];

type Props = {
  onViewChange: (view: DetailPanelView | null) => void;
  patient: Patient;
  selectedVitalIdentifier?: string;
};

export const Vitals = ({ onViewChange, patient, selectedVitalIdentifier }: Props): React.ReactElement => {
  const { t } = useTranslation('records');
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState<RecordsListFilters>(defaultFilters);
  const handleFiltersChange = useDebouncedCallback(setFilters, 300);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedVitalType, setSelectedVitalType] = useState<ValueSetCode | undefined>(undefined);
  useEffect(() => {
    if (!isAddOpen) setSelectedVitalType(undefined);
  }, [isAddOpen]);

  const vitalType = useValueSetExpand({
    queryOptions: (filter) => trpc.valueSet.expand.queryOptions({ url: vitalSignsValueSet.url ?? '', filter }),
    enabled: isAddOpen,
  });

  const lookupQuery = useQuery({
    ...trpc.codeSystem.lookup.queryOptions({
      system: selectedVitalType?.system ?? '',
      code: selectedVitalType?.code ?? '',
      properties: ['EXAMPLE_UCUM_UNITS'],
    }),
    enabled: Boolean(selectedVitalType?.system && selectedVitalType.code && selectedVitalType.code !== BP_PANEL_CODE),
  });

  const lookupProps = selectedVitalType
    ? parseVitalLookupProps(lookupQuery.data?.properties)
    : { unit: undefined, unitDisplay: undefined };

  const listOptions = {
    ownership: filters.ownership,
    searchParams: { 'code:text': filters.searchQuery },
    status: filters.status,
  };

  const {
    items: observations,
    query,
    bottomSentinelRef,
  } = useInfiniteRecordList({
    queryKey: trpc.patient.observation.list.queryKey({
      category: 'vitals',
      medplumPatientId: patient.medplumId,
      options: listOptions,
    }),
    queryFn: async ({ offset, count }) => {
      const res = await trpcClient.patient.observation.list.query({
        category: 'vitals',
        medplumPatientId: patient.medplumId,
        options: listOptions,
        offset,
        count,
      });
      return { items: res.observations, hasMore: res.hasMore };
    },
  });

  const { mutateAsync: createObservation, isPending: isCreating } = useMutation(
    trpc.patient.observation.create.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: trpc.patient.observation.list.queryKey({
            category: 'vitals',
            medplumPatientId: patient.medplumId,
          }),
        });
      },
    }),
  );

  const handleCreateVital = useCallback(
    async (values: AddVitalFormValues): Promise<void> => {
      const category = [
        {
          coding: [
            {
              system: 'http://terminology.hl7.org/CodeSystem/observation-category',
              code: 'vital-signs',
              display: 'Vital Signs',
            },
          ],
        },
      ];
      const code = {
        coding: [{ system: 'http://loinc.org', code: values.vitalType.code, display: values.vitalType.display }],
      };
      const subject = { reference: `Patient/${patient.medplumId}` };
      const effectiveDateTime = parseISO(values.date).toISOString();

      try {
        if ('systolic' in values) {
          await createObservation({
            resource: {
              resourceType: 'Observation',
              status: 'final',
              category,
              code,
              subject,
              effectiveDateTime,
              component: [
                {
                  code: {
                    coding: [
                      { system: 'http://loinc.org', code: BP_SYSTOLIC_CODE, display: 'Systolic blood pressure' },
                    ],
                  },
                  valueQuantity: {
                    value: values.systolic,
                    unit: 'mmHg',
                    system: 'http://unitsofmeasure.org',
                    code: 'mm[Hg]',
                  },
                },
                {
                  code: {
                    coding: [
                      { system: 'http://loinc.org', code: BP_DIASTOLIC_CODE, display: 'Diastolic blood pressure' },
                    ],
                  },
                  valueQuantity: {
                    value: values.diastolic,
                    unit: 'mmHg',
                    system: 'http://unitsofmeasure.org',
                    code: 'mm[Hg]',
                  },
                },
              ],
            },
          });
        } else {
          await createObservation({
            resource: {
              resourceType: 'Observation',
              status: 'final',
              category,
              code,
              subject,
              effectiveDateTime,
              valueQuantity: {
                value: values.value,
                unit: values.unitDisplay,
                system: 'http://unitsofmeasure.org',
                code: values.unit,
              },
            },
          });
        }
        toast.success(t('vitalAdded'));
        setIsAddOpen(false);
      } catch (error) {
        toast.error(`${t('addVitalFailed')}: ${error instanceof Error ? error.message : ''}`);
        throw error;
      }
    },
    [createObservation, patient.medplumId, t],
  );

  return (
    <div className="flex flex-col gap-2 p-2">
      <RecordsListControls
        title={t('vitals')}
        searchPlaceholder={t('searchVitals')}
        onFiltersChange={handleFiltersChange}
        onAdd={() => setIsAddOpen(true)}
        addLabel={t('addVital')}
      />
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('addVital')}</DialogTitle>
          </DialogHeader>
          <AddVitalFormContent
            isCreating={isCreating}
            onClose={() => setIsAddOpen(false)}
            onCreate={handleCreateVital}
            vitalType={vitalType}
            onVitalTypeChange={setSelectedVitalType}
            unit={lookupProps.unit}
            unitDisplay={lookupProps.unitDisplay}
            isUnitLoading={lookupQuery.isFetching}
          />
        </DialogContent>
      </Dialog>
      <RecordListScroll
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        isFetchingNextPage={query.isFetchingNextPage}
        bottomSentinelRef={bottomSentinelRef}
      >
        <VitalsList
          vitals={observations}
          onSelectVital={(vitalIdentifier) => onViewChange({ type: 'vital-detail', vitalIdentifier })}
          selectedVitalIdentifier={selectedVitalIdentifier}
        />
      </RecordListScroll>
    </div>
  );
};
